/**
 * Export ▾ menu chrome (PV18, SE-8360 / PRD-3.4 "Export ▾ (CSV · PDF)").
 *
 * Idle, done and failed render the trigger on the shared
 * `DesktopDropdownMenu`; done adds the written filename, failed adds the
 * message and Retry. While a run is in flight the trigger gives way to a
 * disabled control reading "Exporting… n of N", so the menu takes no second
 * press. The View never reads the platform: the items it receives already
 * say whether a format is available.
 * @module features/scouting/components/WatchlistExportMenu/WatchlistExportMenuView
 */

import {
  ChevronDownIcon,
  DownloadIcon,
  RefreshCwIcon,
  XIcon,
} from "lucide-react-native";
import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Icon } from "@/components/atoms/Icon";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import DesktopDropdownMenu, {
  type DesktopDropdownMenuItem,
} from "@/components/molecules/DesktopDropdownMenu";
import type {
  WatchlistExportProgress,
  WatchlistExportStatus,
} from "@/features/scouting/hooks/useWatchlistExport";
import { TEST_ID_WATCHLIST_EXPORT } from "e2e/testId";

/** Props for {@link WatchlistExportMenuView}. */
export interface WatchlistExportMenuViewProps {
  readonly status: WatchlistExportStatus;
  readonly progress: WatchlistExportProgress;
  readonly error: string | null;
  readonly filename: string | null;
  readonly isMenuOpen: boolean;
  readonly onToggleMenu: () => void;
  readonly onCloseMenu: () => void;
  /** CSV and PDF rows, disabled with a caption where unavailable. */
  readonly items: readonly DesktopDropdownMenuItem[];
  /**
   * PV19: with one format the trigger is a plain button that starts it —
   * "Export PDF" — and no menu opens; `caption` reads "Available on web"
   * on native.
   */
  readonly singleFormat: {
    readonly label: string;
    readonly caption: string | null;
    readonly disabled: boolean;
    readonly onPress: () => void;
  } | null;
  readonly onRetry: () => void;
  /** Stops a run between pages; the retired run writes no file. */
  readonly onCancel: () => void;
  /** SE-8535: the single-format button drops its label (toolbar density 3). */
  readonly iconOnly?: boolean;
}

/** Same outline chrome as the toolbar's Share button. */
const TRIGGER_CLASS =
  "web:hover:bg-surface-raised h-9 flex-row items-center gap-2 rounded-sm border border-outline-strong px-3";

/**
 * "Exporting… n of N" while pages load; the count appears once the first
 * page has told us the total.
 * @param progress - Players collected against the total
 * @returns The busy label.
 */
const busyLabel = (progress: WatchlistExportProgress): string =>
  progress.total > 0
    ? `Exporting… ${progress.loaded} of ${progress.total}`
    : "Exporting…";

/** The trigger's content: download icon, "Export", chevron. */
const TRIGGER_CONTENT = (
  <Stack direction="horizontal" className="items-center gap-2">
    <Icon as={DownloadIcon} size="sm" className="text-content-primary" />
    <Text variant="body" className="text-content-primary">
      Export
    </Text>
    <Icon as={ChevronDownIcon} size="sm" className="text-content-muted" />
  </Stack>
);

/**
 * SE-8535: the trigger at toolbar density 3 — the icons, no "Export" label.
 */
const TRIGGER_CONTENT_ICON_ONLY = (
  <Stack direction="horizontal" className="items-center gap-1">
    <Icon as={DownloadIcon} size="sm" className="text-content-primary" />
    <Icon as={ChevronDownIcon} size="sm" className="text-content-muted" />
  </Stack>
);

/**
 * The disabled control shown while a run is in flight, with Cancel beside it.
 * SE-8535: icon-sized at toolbar density 3, so a run cannot re-wrap the row.
 * @param progress - Players collected against the total
 * @param onCancel - Stops the run between pages
 * @param iconOnly - Whether both controls drop their labels
 * @returns The busy button and Cancel.
 */
const renderBusy = (
  progress: WatchlistExportProgress,
  onCancel: () => void,
  iconOnly: boolean
) => (
  <Stack direction="horizontal" className="items-center gap-2">
    <Button
      testID={TEST_ID_WATCHLIST_EXPORT.BUSY}
      label={iconOnly ? undefined : busyLabel(progress)}
      iconOnly={iconOnly}
      accessibilityLabel={busyLabel(progress)}
      icon={DownloadIcon}
      variant="outline"
      size="sm"
      className="h-9 rounded-sm"
      isDisabled
    />
    {/* With no visible progress text, a polite live region announces each
        page (CodeRabbit on #7616) — a disabled button's label is not one. */}
    {iconOnly ? (
      <Box
        testID={TEST_ID_WATCHLIST_EXPORT.PROGRESS}
        accessibilityLiveRegion="polite"
        className="sr-only"
      >
        <Text variant="caption">{busyLabel(progress)}</Text>
      </Box>
    ) : null}
    {/* A long or stalled run must be escapable (CodeRabbit on #7396): cancel
        stops between pages and the retired run writes no file. */}
    <Button
      testID={TEST_ID_WATCHLIST_EXPORT.CANCEL}
      label={iconOnly ? undefined : "Cancel"}
      iconOnly={iconOnly}
      icon={iconOnly ? XIcon : undefined}
      accessibilityLabel={iconOnly ? "Cancel export" : "Cancel"}
      onPress={onCancel}
      variant="outline"
      size="sm"
      className="h-9 rounded-sm"
    />
  </Stack>
);

/**
 * "Exported {filename}" once the file is written — announced, not shown.
 * The visible caption beside the trigger crowded the toolbar and repeated
 * what the browser's own download already says (Raniel, SE-8526); a screen
 * reader still hears that the export finished.
 * @param filename - The written filename
 * @returns The announcement.
 */
const renderDone = (filename: string) => (
  <Box
    testID={TEST_ID_WATCHLIST_EXPORT.DONE}
    accessibilityLiveRegion="polite"
    className="sr-only"
  >
    <Text variant="caption">{`Exported ${filename}`}</Text>
  </Box>
);

/**
 * The failure line and Retry beside the trigger.
 * SE-8535: at toolbar density 3 Retry is an icon (the error stays, one line).
 * @param error - The failure message
 * @param onRetry - Restarts the export
 * @param iconOnly - Whether Retry drops its label
 * @returns The caption and the Retry button.
 */
const renderFailed = (
  error: string | null,
  onRetry: () => void,
  iconOnly: boolean
) => (
  <>
    <Text
      testID={TEST_ID_WATCHLIST_EXPORT.FAILED}
      variant="caption"
      // Shrinks inside the row, so a long error cannot widen the cluster
      // and re-wrap the toolbar (CodeRabbit on #7616).
      className="shrink text-status-error"
      numberOfLines={1}
    >
      {`Export failed: ${error ?? "unknown error"}`}
    </Text>
    <Button
      testID={TEST_ID_WATCHLIST_EXPORT.RETRY}
      label={iconOnly ? undefined : "Retry"}
      iconOnly={iconOnly}
      icon={iconOnly ? RefreshCwIcon : undefined}
      accessibilityLabel={iconOnly ? "Retry export" : "Retry"}
      onPress={onRetry}
      variant="outline"
      size="sm"
      className="h-9 rounded-sm"
    />
  </>
);

/**
 * The single-format trigger: a plain button, captioned where unavailable.
 * @param single - The single format
 * @param iconOnly - Whether the button drops its label
 * @returns The button, with its caption.
 */
const renderSingleTrigger = (
  single: NonNullable<WatchlistExportMenuViewProps["singleFormat"]>,
  iconOnly: boolean
) => (
  <>
    <Button
      testID={TEST_ID_WATCHLIST_EXPORT.TRIGGER}
      label={iconOnly ? undefined : single.label}
      iconOnly={iconOnly}
      accessibilityLabel={single.label}
      icon={DownloadIcon}
      onPress={single.onPress}
      variant="outline"
      size="sm"
      className="h-9 rounded-sm"
      isDisabled={single.disabled}
    />
    {single.caption ? (
      <Text variant="caption" className="text-content-muted">
        {single.caption}
      </Text>
    ) : null}
  </>
);

/**
 * Renders the Export ▾ control for one export state.
 * @param props - View props
 * @param props.status - Where the operation is
 * @param props.progress - Players collected against the total
 * @param props.error - The failure message while failed
 * @param props.filename - The written filename while done
 * @param props.isMenuOpen - Whether the menu is open
 * @param props.onToggleMenu - Toggles the menu
 * @param props.onCloseMenu - Closes the menu
 * @param props.items - CSV and PDF rows
 * @param props.singleFormat - The one format, when the control is a plain button
 * @param props.iconOnly - SE-8535: every export control drops its label
 * @param props.onRetry - Restarts the failed export
 * @param props.onCancel - Stops the run in flight
 */
const WatchlistExportMenuView = ({
  status,
  progress,
  error,
  filename,
  isMenuOpen,
  onToggleMenu,
  onCloseMenu,
  items,
  singleFormat,
  iconOnly = false,
  onRetry,
  onCancel,
}: WatchlistExportMenuViewProps) =>
  status === "collecting" || status === "rendering" ? (
    renderBusy(progress, onCancel, iconOnly)
  ) : (
    <Stack direction="horizontal" className="items-center gap-2">
      {singleFormat ? (
        renderSingleTrigger(singleFormat, iconOnly)
      ) : (
        <DesktopDropdownMenu
          isOpen={isMenuOpen}
          onClose={onCloseMenu}
          onToggle={onToggleMenu}
          triggerTestID={TEST_ID_WATCHLIST_EXPORT.TRIGGER}
          triggerAccessibilityLabel="Export"
          triggerClassName={TRIGGER_CLASS}
          triggerContent={
            iconOnly ? TRIGGER_CONTENT_ICON_ONLY : TRIGGER_CONTENT
          }
          items={items}
        />
      )}
      {status === "done" && filename ? renderDone(filename) : null}
      {status === "failed" ? renderFailed(error, onRetry, iconOnly) : null}
    </Stack>
  );

WatchlistExportMenuView.displayName = "WatchlistExportMenuView";

export default memo(WatchlistExportMenuView);
